import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/', label: 'Home', type: 'page' },
    { href: 'trama', label: 'Storia', type: 'anchor' },
    { href: 'trailer', label: 'Gallery', type: 'anchor' },
    { href: 'prossime-date', label: 'Prenota', type: 'anchor' },
    { href: 'teatri', label: 'Per i Teatri', type: 'anchor' },
    { href: '/info', label: 'Info & FAQ', type: 'page' },
  ];

  const handleNavClick = (e, link) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Chiudi prima il menu
    setIsMobileMenuOpen(false);
    
    // Poi esegui la navigazione con un piccolo delay
    setTimeout(() => {
      if (link.type === 'anchor') {
        if (location.pathname === '/') {
          // Siamo già sulla home, scrolla alla sezione
          const element = document.getElementById(link.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        } else {
          // Siamo su un'altra pagina, naviga alla home con hash
          navigate(`/#${link.href}`);
        }
      } else if (link.type === 'page') {
        if (link.href === '/') {
          if (location.pathname === '/') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            navigate('/');
          }
        } else {
          navigate(link.href);
        }
      }
    }, 50);
  };

  const handleLogoClick = (e) => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass py-3' : 'bg-transparent py-4'
        }`}
      >
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2"
            data-testid="logo-link"
            onClick={handleLogoClick}
          >
            <span className="font-display text-2xl md:text-3xl text-[#FFD700] tracking-wider">
              SISTER TAC
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={(e) => handleNavClick(e, link)}
                className="nav-link text-sm uppercase tracking-widest cursor-pointer bg-transparent border-none"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white z-[110]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Chiudi menu' : 'Apri menu'}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-[100] bg-[#0a0a0a] flex flex-col items-center justify-center"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={(e) => handleNavClick(e, link)}
                className="text-2xl font-semibold text-white hover:text-[#FFD700] transition-colors cursor-pointer bg-transparent border-none"
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
